// src/integrations/trustarc/index.js
import React from "react"
import PropTypes from "prop-types"
import { createTrustArcComponents } from "@trustarc/react-cookie-consent-manager"
import ClientOnly from "../../components/client-only"

const TRUST_ARC_CM_ID = process.env.GATSBY_TRUSTARC_CM_ID || "ginzi8"
const TRUST_ARC_ENABLED = process.env.GATSBY_TRUSTARC_REACT_ENABLED !== "false"
const TRUST_ARC_DEBUG = process.env.GATSBY_TRUSTARC_DEBUG === "true"
const TRUST_ARC_CCM_VERSION = (process.env.GATSBY_TRUSTARC_CCM_VERSION || "advanced").toLowerCase()
const TRUST_ARC_BANNER_ID = TRUST_ARC_CCM_VERSION === "pro" ? "consent-banner" : "consent_blackbar"

const logDebug = (...args) => {
  if (TRUST_ARC_DEBUG) {
    console.log(...args)
  }
}

let trustArcComponents
const getTrustArcComponents = () => {
  if (!TRUST_ARC_ENABLED || !TRUST_ARC_CM_ID) return null

  if (!trustArcComponents) {
    trustArcComponents = createTrustArcComponents(TRUST_ARC_CM_ID, {
      cookiePreferencesContainer: "teconsent",
      ccmVersion: TRUST_ARC_CCM_VERSION,
      debug: TRUST_ARC_DEBUG
    })
  }

  return trustArcComponents
}

const SafeTrustArcConsentBanner = () => {
  React.useEffect(() => {
    const element = document.getElementById(TRUST_ARC_BANNER_ID)
    if (!element) return

    // TrustArc package calls bannerMain() unguarded; this avoids app crashes.
    const bannerMain = window?.truste?.bn?.bannerMain
    const reopenBanner = window?.truste?.bn?.reopenBanner

    if (element.childNodes.length === 0) {
      if (typeof bannerMain === "function") {
        bannerMain()
        logDebug("Called window.truste.bn.bannerMain() from safe banner.")
      } else if (typeof reopenBanner === "function") {
        reopenBanner()
        logDebug("Called window.truste.bn.reopenBanner() from safe banner.")
      } else {
        logDebug("TrustArc banner API not ready yet; leaving placeholder for script hydration.")
      }
    }
  }, [])

  return <div id={TRUST_ARC_BANNER_ID} />
}

export const TrustArcScriptAndBanner = () => {
  const trustArcComponents = getTrustArcComponents()
  if (!trustArcComponents) return null

  const { TrustArcScript } = trustArcComponents
  return (
    <ClientOnly>
      <>
        <TrustArcScript />
        <SafeTrustArcConsentBanner />
      </>
    </ClientOnly>
  )
}

export const TrustArcBanner = () => {
  const trustArcComponents = getTrustArcComponents()
  if (!trustArcComponents) return null

  return (
    <ClientOnly>
      <SafeTrustArcConsentBanner />
    </ClientOnly>
  )
}

export const TrustArcFooterLinks = ({ showIrmLink }) => {
  const trustArcComponents = getTrustArcComponents()
  if (!trustArcComponents) return null

  const { TrustArcCookiePreferencesLink, TrustArcIRMLink } = trustArcComponents

  return (
    <>
      <li>
        <ClientOnly>
          <TrustArcCookiePreferencesLink />
        </ClientOnly>
      </li>
      {showIrmLink && (
        <li>
          <ClientOnly>
            <TrustArcIRMLink />
          </ClientOnly>
        </li>
      )}
    </>
  )
}

TrustArcFooterLinks.propTypes = {
  showIrmLink: PropTypes.bool
}

TrustArcFooterLinks.defaultProps = {
  showIrmLink: true
}
